export interface ModeOptionsState {
  rememberState: boolean;
  searchEnabled: boolean;
  aiEnabled: boolean;
}

const STORAGE_KEY = 'fias.make-a-request.modeOptions';

// When Remember is off, search/AI values persist for this long before being
// forgotten — i.e. a quick reload keeps them, but coming back hours later
// resets to defaults (both on).
export const MODE_OPTIONS_TEMP_TTL_MS = 30 * 60 * 1000;

export const DEFAULT_MODE_OPTIONS: ModeOptionsState = {
  rememberState: false,
  searchEnabled: true,
  aiEnabled: true,
};

export function readInitialModeOptions(now: number = Date.now()): ModeOptionsState {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return DEFAULT_MODE_OPTIONS;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_MODE_OPTIONS;
    const parsed = JSON.parse(raw) as Partial<ModeOptionsState> & { updatedAt?: number };
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_MODE_OPTIONS;

    const rememberState = parsed.rememberState === true;
    const searchEnabled = parsed.searchEnabled !== false;
    const aiEnabled = parsed.aiEnabled !== false;

    if (rememberState) {
      return { rememberState: true, searchEnabled, aiEnabled };
    }

    // Not remembered — only restore if within the TTL.
    const updatedAt = typeof parsed.updatedAt === 'number' ? parsed.updatedAt : 0;
    if (now - updatedAt > MODE_OPTIONS_TEMP_TTL_MS) {
      return { ...DEFAULT_MODE_OPTIONS };
    }
    return { rememberState: false, searchEnabled, aiEnabled };
  } catch {
    return DEFAULT_MODE_OPTIONS;
  }
}

export function persistModeOptions(state: ModeOptionsState, now: number = Date.now()): void {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, updatedAt: now }));
  } catch {
    // Swallow — preference persistence is best-effort.
  }
}
